import React, { useState, useRef, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import {
    CheckSquare, Play, Pause, Copy, Trash2, X, ChevronDown, Archive, RotateCcw
} from 'lucide-react';

// ─── Portal dropdown — renders at document.body to escape overflow:hidden ────
function PortalDropdown({ anchorRef, open, onClose, children }) {
    const [pos, setPos] = useState({ top: 0, left: 0, width: 0 });

    useEffect(() => {
        if (!open || !anchorRef.current) return;

        const updatePos = () => {
            const rect = anchorRef.current?.getBoundingClientRect();
            if (!rect) return;
            setPos({
                top: rect.bottom + window.scrollY + 6,
                left: rect.left + window.scrollX,
                width: rect.width,
            });
        };

        updatePos();
        window.addEventListener('resize', updatePos);
        window.addEventListener('scroll', updatePos, true);
        return () => {
            window.removeEventListener('resize', updatePos);
            window.removeEventListener('scroll', updatePos, true);
        };
    }, [open, anchorRef]);

    if (!open) return null;

    return ReactDOM.createPortal(
        <>
            {/* Invisible backdrop to close on outside click */}
            <div className="fixed inset-0 z-[999]" onClick={onClose} />
            <div
                style={{ position: 'absolute', top: pos.top, left: pos.left, minWidth: 176, zIndex: 1000 }}
                className="bg-surface dark:bg-dark-800 border border-line-subtle dark:border-dark-700 rounded-xl shadow-2xl py-1.5 overflow-hidden"
            >
                {children}
            </div>
        </>,
        document.body
    );
}

// ─── Main component ───────────────────────────────────────────────────────────
const CampaignBulkOperations = ({
    selectedCount,
    selectedCampaigns = [],
    onBulkAction,
    onClearSelection
}) => {
    const [showStatusDropdown, setShowStatusDropdown] = useState(false);
    const [showMoreDropdown, setShowMoreDropdown] = useState(false);

    const statusBtnRef = useRef(null);
    const moreBtnRef = useRef(null);

    const statusOptions = [
        { value: 'active',   label: 'Activate', icon: Play,    accent: 'text-emerald-500 dark:text-emerald-400', hover: 'hover:bg-emerald-50 dark:hover:bg-emerald-900/10' },
        { value: 'paused',   label: 'Pause',     icon: Pause,   accent: 'text-amber-500 dark:text-amber-400',    hover: 'hover:bg-amber-50 dark:hover:bg-amber-900/10' },
        { value: 'archived', label: 'Archive',   icon: Archive, accent: 'text-content-muted dark:text-gray-400',     hover: 'hover:bg-gray-50 dark:hover:bg-dark-700' },
    ];

    const handleStatusChange = (status) => {
        onBulkAction('status', { status });
        setShowStatusDropdown(false);
    };

    const handleBulkDuplicate = () => {
        onBulkAction('duplicate', {});
        setShowMoreDropdown(false);
    };

    const handleBulkDelete = () => {
        onBulkAction('delete', {});
        setShowMoreDropdown(false);
    };

    const canActivate = selectedCampaigns.some(c => c.status === 'paused' || c.status === 'draft');
    const canPause    = selectedCampaigns.some(c => c.status === 'active' || c.status === 'scheduled');
    const canArchive  = selectedCampaigns.some(c => c.status !== 'archived');

    const closeAll = useCallback(() => {
        setShowStatusDropdown(false);
        setShowMoreDropdown(false);
    }, []);

    return (
        <div className="relative mb-4 bg-gradient-to-r from-blue-50/80 to-indigo-50/50 dark:from-blue-900/10 dark:to-indigo-900/10 dark:bg-dark-800 rounded-xl border border-blue-200/70 dark:border-blue-800/40 shadow-sm">
            {/* Accent bar */}
            <div className="absolute top-0 inset-x-0 h-[2px] rounded-t-xl bg-gradient-to-r from-blue-500 to-indigo-500" />

            <div className="px-4 py-3 flex items-center gap-3 flex-wrap">

                {/* Left — selection count badge */}
                <div className="flex items-center gap-2.5 mr-1 flex-shrink-0">
                    <div className="w-7 h-7 rounded-lg bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                        <CheckSquare className="w-3.5 h-3.5 text-blue-600 dark:text-blue-400" />
                    </div>
                    <div className="leading-none whitespace-nowrap">
                        <span className="text-[13px] font-bold text-blue-700 dark:text-blue-300">{selectedCount}</span>
                        <span className="text-[13px] font-medium text-blue-600/80 dark:text-blue-400/80 ml-1">
                            {selectedCount !== 1 ? 'campaigns' : 'campaign'} selected
                        </span>
                    </div>
                </div>

                {/* Vertical divider */}
                <div className="h-5 w-px bg-blue-200 dark:bg-blue-800/60 flex-shrink-0" />

                {/* Action buttons */}
                <div className="flex items-center gap-2 flex-wrap flex-1">

                    {/* ── Change Status ── */}
                    <div className="relative">
                        <button
                            ref={statusBtnRef}
                            onClick={() => { setShowStatusDropdown(p => !p); setShowMoreDropdown(false); }}
                            className="flex items-center gap-1.5 h-8 px-3 rounded-lg text-meta font-semibold bg-surface dark:bg-dark-700 border border-line dark:border-dark-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-dark-600 hover:border-line-strong dark:hover:border-dark-500 transition-all duration-150 shadow-sm whitespace-nowrap"
                        >
                            <RotateCcw className="w-3.5 h-3.5 text-blue-500 dark:text-blue-400" />
                            Change Status
                            <ChevronDown className={`w-3.5 h-3.5 text-content-subtle transition-transform duration-200 ${showStatusDropdown ? 'rotate-180' : ''}`} />
                        </button>

                        <PortalDropdown anchorRef={statusBtnRef} open={showStatusDropdown} onClose={closeAll}>
                            <div className="px-3 py-1.5 mb-0.5">
                                <span className="text-[10px] font-bold uppercase tracking-wider text-content-subtle dark:text-gray-500">Set Status To</span>
                            </div>
                            {statusOptions.map((opt) => {
                                const Icon = opt.icon;
                                const disabled =
                                    (opt.value === 'active'   && !canActivate) ||
                                    (opt.value === 'paused'   && !canPause)    ||
                                    (opt.value === 'archived' && !canArchive);
                                return (
                                    <button
                                        key={opt.value}
                                        onClick={() => !disabled && handleStatusChange(opt.value)}
                                        disabled={disabled}
                                        className={`w-full flex items-center gap-2.5 px-3 py-2 text-left text-[12px] font-medium transition-colors ${opt.hover} ${disabled ? 'opacity-35 cursor-not-allowed' : 'cursor-pointer'}`}
                                    >
                                        <Icon className={`w-3.5 h-3.5 flex-shrink-0 ${opt.accent}`} />
                                        <span className="text-gray-700 dark:text-gray-300">{opt.label}</span>
                                    </button>
                                );
                            })}
                        </PortalDropdown>
                    </div>

                    {/* ── More Actions ── */}
                    <div className="relative">
                        <button
                            ref={moreBtnRef}
                            onClick={() => { setShowMoreDropdown(p => !p); setShowStatusDropdown(false); }}
                            className="flex items-center gap-1.5 h-8 px-3 rounded-lg text-meta font-semibold bg-surface dark:bg-dark-700 border border-line dark:border-dark-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-dark-600 hover:border-line-strong dark:hover:border-dark-500 transition-all duration-150 shadow-sm whitespace-nowrap"
                        >
                            More Actions
                            <ChevronDown className={`w-3.5 h-3.5 text-content-subtle transition-transform duration-200 ${showMoreDropdown ? 'rotate-180' : ''}`} />
                        </button>

                        <PortalDropdown anchorRef={moreBtnRef} open={showMoreDropdown} onClose={closeAll}>
                            <button
                                onClick={handleBulkDuplicate}
                                className="w-full flex items-center gap-2.5 px-3 py-2 text-left text-[12px] font-medium text-gray-700 dark:text-gray-300 hover:bg-blue-50 dark:hover:bg-blue-900/10 transition-colors"
                            >
                                <Copy className="w-3.5 h-3.5 text-blue-500 dark:text-blue-400 flex-shrink-0" />
                                Duplicate Selected
                            </button>
                            <div className="my-1 mx-2 border-t border-line-subtle dark:border-dark-700" />
                            <button
                                onClick={handleBulkDelete}
                                className="w-full flex items-center gap-2.5 px-3 py-2 text-left text-[12px] font-medium text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/10 transition-colors"
                            >
                                <Trash2 className="w-3.5 h-3.5 flex-shrink-0" />
                                Delete Selected
                            </button>
                        </PortalDropdown>
                    </div>
                </div>

                {/* Right — clear button */}
                <button
                    onClick={onClearSelection}
                    className="flex items-center gap-1.5 h-8 px-3 rounded-lg text-[12px] font-semibold text-content-muted dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-surface dark:hover:bg-dark-700 border border-transparent hover:border-line dark:hover:border-dark-600 transition-all duration-150 flex-shrink-0"
                >
                    <X className="w-3.5 h-3.5" />
                    Clear
                </button>
            </div>
        </div>
    );
};

export default CampaignBulkOperations;
